import { z } from "zod";

import { LICENSE_STATUSES } from "../billing";

/**
 * ABONELİK VE KULLANIM — GENEL BAKIŞ (CLOS-197 `[173]`).
 *
 * ### ⚠ BU YÜZEY YALNIZ PROJEKSİYON
 *
 * Kartın kapsam sınırı: *"Genel Bakış yalnız projection'dır. Paket, lisans, hak, kullanım,
 * ödeme veya plan değişikliği gerçeğini hesaplamaz ve değiştirmez."* Bu yüzden şemada
 * hiçbir yazma girdisi yok — ayrım kod düzeyinde yapısal: yazma şeması olmayan bir
 * sözleşmeden yazma ucu doğmaz. Plan/ödeme komutları CLOS-202/203'ün konusu.
 *
 * ### ⚠ GÖSTERİLMEYENLER ve NEDEN — uydurmamak bir tasarım kararı
 *
 * Kart açıkça diyor: *"henüz desteklenmeyen değer uydurulmaz"*, *"olmayan özellik
 * aktifmiş gibi gösterilmez."* Ölçüldü (1 Eylül 2026), üç alan BİLEREK yok:
 *
 * - **Fiyat ve para birimi.** Kartın açık kararı bunları ürün konfigürasyonuna bırakıyor
 *   (*"Plan adı, fiyat, para birimi, dönem ... konfigürasyondan gelir"*) — ama
 *   `config/packages.json` bugün fiyat alanı TAŞIMIYOR ve `packages` tablosunda da yok.
 *   Yani veri hiç mevcut değil. Boş bir "₺—" alanı çizmek, ürünün ücretli olduğunu ima
 *   edip değerini gizlemek olurdu.
 * - **Depolama ve AI sayaçları** — CLOS-200 `[174]` henüz Yapılacaklar'da.
 * - **Ek haklar** — CLOS-201 `[175]` henüz Yapılacaklar'da. Kart temel hak ile ek hakkın
 *   *"aynı kayıt gibi sunulmaması"* diyor; ek hak kaynağı yokken boş bir bölüm çizmek,
 *   olmayan bir ayrımı varmış gibi göstermek olurdu.
 *
 * Bu alanlar kaynakları geldiğinde EKLENİR. Bugün şemada yer tutucu bırakmıyoruz: tip
 * seviyesinde var olan bir alan, arayüzde er ya da geç çizilir.
 */

/** Sayısal ya da mantıksal hak sınırı; `null` = sınırsız. */
export const usageLimitSchema = z.object({
  used: z.number().int().min(0),
  /** ⚠ `null` SINIRSIZ demek, "bilinmiyor" değil — ikisini karıştırmak sınırı silerdi. */
  limit: z.number().int().min(0).nullable(),
});
export type UsageLimit = z.infer<typeof usageLimitSchema>;

export const subscriptionOverviewSchema = z.object({
  /**
   * Abonelik KİMLİĞİ (CLOS-286 `[181]`, alt görev CLOS-975).
   *
   * ⚠ Yalnız D06 bağlamsal Aktivite geçmişinin (`GET /api/activity?entityType=subscription`)
   * `entityId` parametresi için — istemci başka hiçbir yerde bu kimliği kullanmıyor ve
   * abonelik dışı bir sağlayıcı referansı DEĞİL.
   */
  id: z.uuid(),
  /**
   * Paket kimliği DEĞİL, kullanıcıya görünen ad ve açıklama.
   *
   * ⚠ İkisi de konfigürasyondan seed'lenen `packages` satırından geliyor; kod bir plan adı
   * UYDURMUYOR (kartın açık kararı).
   */
  packageCode: z.string(),
  packageName: z.string(),
  packageDescription: z.string(),

  status: z.enum(LICENSE_STATUSES),
  /** Yürürlükteki dönem — kartın *"etkili dönem"* kriteri. */
  currentPeriodStart: z.string(),
  currentPeriodEnd: z.string(),
  /** Deneme bitişi; deneme yoksa `null`. */
  trialEndsAt: z.string().nullable(),

  /**
   * Bekleyen plan değişikliği — kartın *"planlanmış değişiklik bilgisi"* kriteri.
   *
   * ⚠ Yalnız UYGULANMAMIŞ olan dönüyor. Uygulanmışları da döndürseydik kullanıcı geçmiş
   * bir değişikliği yaklaşan bir değişiklik sanardı.
   */
  scheduledChange: z.object({ packageName: z.string(), effectiveAt: z.string() }).nullable(),

  /**
   * Desteklenen kullanım sayaçları.
   *
   * ⚠ Bu nesne yalnız KAYNAĞI OLAN sayaçları taşıyor (CLOS-198 sözleşme kapasitesi,
   * CLOS-199 iç kullanıcı koltuğu, KARAR 04 AI kredisi). Depolama burada hâlâ YOK —
   * kaynağı yazılmadı ve olmayan bir sayacı sıfır göstermek, kullanımın sıfır olduğunu
   * söylemek olurdu.
   *
   * ### ⚠ `aiCredits` EKLENDİ ve bu bir KARAR DEĞİŞİKLİĞİ
   *
   * Bu şema bir süre AI sayacını BİLEREK taşımıyordu ve gerekçesi yazılıydı:
   * *"CLOS-200 `[174]` henüz Yapılacaklar'da"*. O gerekçe artık geçerli değil — kaynak
   * yazıldı (`ai_credit_periods`/`ai_credit_operations`, KARAR 04) ve motor onu harcıyor.
   * Alanı kapalı tutmak, kullanıcının PARASINI harcayan bir sayacı ondan gizlemek olurdu:
   * kredisi biten kullanıcı bunu ancak analiz reddedildiğinde öğrenirdi.
   *
   * ⚠ Sayının kaynağı hak motoru DEĞİL dönem satırı (`lib/aiCreditUsage.ts`): tavanı
   * uygulayan şey `ai_credit_periods` üzerindeki `CHECK` kısıtı ve ekran, motorun izin
   * verdiğinden başka bir sayı göstermemeli.
   */
  usage: z.object({
    contracts: usageLimitSchema,
    /**
     * DÖNEMSEL sözleşme OLUŞTURMA hakkı (CLOS-836 · alt görev CLOS-973).
     *
     * ⚠ `contracts` ile AYRI KOTA, aynı sayının iki görünümü DEĞİL. `contracts` durum
     * bazlı: arşivlenmemiş her kayıt tüketiyor, arşivleme serbest bırakıyor. Bu sayaç
     * zaman bazlı: abonelik dönemiyle yenileniyor ve harcandığında geri gelmiyor.
     *
     * Tek alanda birleştirseydik "ayda 5 sözleşme" satan bir paket, kullanıcı arşivledikçe
     * sınırsıza dönerdi — kotanın kendisi silinirdi.
     */
    contractCreations: usageLimitSchema,
    internalSeats: usageLimitSchema,
    aiCredits: usageLimitSchema,
  }),
});
export type SubscriptionOverview = z.infer<typeof subscriptionOverviewSchema>;

/**
 * ABONELİK YAŞAM DÖNGÜSÜ COMMAND ŞEMALARI (CLOS-203 `[177]`/CLOS-255 `[179]`).
 *
 * ⚠ Yükseltme (CLOS-254) ve Sponsorlu Alan dönüşümü (CLOS-256) burada YOK: ikisi de
 * DOĞRULANMIŞ bir ödeme sonucu gerektiriyor ve iyzico adapter'ı bugün KAPALI
 * (`apps/api/src/lib/billing/iyzico.ts`). Bir HTTP ucu istemciden gelen "ödeme oldu"
 * iddiasını asla kabul edemez (KARAR 03: "senkron kullanıcı dönüşü ödeme kanıtı
 * değildir") — bu yüzden `activateRenewalAfterPayment`/`upgradeSubscriptionPlan`/
 * `convertSponsoredWorkspaceToEnterprise` yalnız CLOS-202'nin server-to-server doğrulama
 * akışının çağırdığı İÇ seam'lerdir, kendi HTTP ucu YOK.
 */

/** Boş gövde — abonelik aktif çalışma alanı bağlamından çözülüyor, gövdede taşınmıyor. */
export const subscriptionCommandBodySchema = z.strictObject({});

export const subscriptionUpgradePreviewQuerySchema = z.object({
  targetPackageCode: z.string().min(1, "Hedef paket kodu zorunludur"),
});

export const subscriptionDowngradePreviewQuerySchema = subscriptionUpgradePreviewQuerySchema;

export const subscriptionScheduleDowngradeSchema = z.strictObject({
  targetPackageCode: z.string().min(1, "Hedef paket kodu zorunludur"),
});

export const entitlementDiffSchema = z.object({
  key: z.string(),
  from: z.union([z.number(), z.boolean(), z.literal("unlimited")]).optional(),
  to: z.union([z.number(), z.boolean(), z.literal("unlimited")]).optional(),
});

export const planChangePreviewSchema = z.object({
  currentPackageCode: z.string(),
  targetPackageCode: z.string(),
  isUpgrade: z.boolean(),
  changes: z.array(entitlementDiffSchema),
});
export type PlanChangePreviewResponse = z.infer<typeof planChangePreviewSchema>;

export const downgradeOverageSchema = z.object({
  key: z.string(),
  currentUsage: z.number().int().min(0),
  targetLimit: z.number().int().min(0),
});

export const downgradeImpactSchema = z.object({
  currentPackageCode: z.string(),
  targetPackageCode: z.string(),
  overages: z.array(downgradeOverageSchema),
});
export type DowngradeImpactResponse = z.infer<typeof downgradeImpactSchema>;
